import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Typography, Breadcrumbs, Link, Box, Tabs, Tab, Alert, Divider } from '@mui/material';
import {
    ArrowBack as ArrowBackIcon, Contacts as ContactsIcon, SellOutlined as StatusIcon,
    NewReleasesOutlined as ErrorIcon, CheckCircleOutline as SuccessIcon,
} from '@mui/icons-material';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useAuth } from '@contexts/AuthContext';
import API from '@utils/axios';
import Layout from '@components/Layout';
import SendoutEditionForm from '@components/sendout/SendoutEditionForm';
import FormSkeleton from '@components/FormSkeleton';
import SendoutName from '@components/sendout/SendoutName';
import ContactTable from '@components/contact/ContactTable';
import MessageTable from '@components/message/MessageTable';
import TabIconBadge from '@components/TabIconBadge';
import PackTypeIcon from '@components/pack/PackTypeIcon';

function SendoutEditPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showMessage } = useSnackbar();
    const { refreshAuth } = useAuth();
    const [initialData, setInitialData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [num, setNum] = useState(0);
    const [tab, setTab] = useState(searchParams.get('tab') || 'status.all');
    const [tabType, setTabType] = useState(null);
    const [tabSubType, setTabSubType] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            const response = await API.get(`/sendouts/${id}`);
            setInitialData(response.data);
        } catch (error) {
            showMessage(t('Failed to load data'), 'error');
        } finally {
            setLoaded(true);
        }
    }, [id, t, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRefresh = () => {
        fetchData(); // Reload du sendout
        setNum(num + 1); // Reload du tableau de contacts
    }

    const handleSubmit = async (data) => {
        try {
            // Si data.planned_at est null, on ne l'envoie pas
            if (data.planned_at === null) {
                delete data.planned_at;
            }
            const response = await API.put(`/sendouts/${id}`, data);
            if (response.status === 200) {
                setInitialData({ ...initialData, ...response.data });
                showMessage(t('Modification saved'), 'success');
                refreshAuth();
            }
        } catch (error) {
            console.error(error);
            showMessage(t(error.response.data.message), 'error');
        }
    };

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
        setSearchParams({ tab: newValue });
    };

    useEffect(() => {
        const [type, subType] = tab.split('.');
        setTabType(type);
        setTabSubType(subType);
    }, [tab]);

    const onContactClick = (params) => {
        navigate(`/sendouts/${id}/contacts/${params.row.id}?tab=${encodeURIComponent(tab)}`);
    }

    const onMessageClick = (params) => {
        console.log(params);
        navigate(`/sendouts/${id}/contacts/${params.row.contact_id}?tab=${encodeURIComponent(tab)}&message=${encodeURIComponent(params.row.id)}`);
    }

    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to="/sendouts" underline="hover">{t('Sendout tracking')}</Link>
                    <Typography>{loaded ? <SendoutName row={initialData} links={true} /> : ''}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to="/sendouts" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            {loaded ?
                <SendoutEditionForm formData={initialData} onSubmit={handleSubmit} handleRefresh={handleRefresh} />
                : <FormSkeleton nb={1} />
            }

            <Tabs value={tab} sx={{ mt: 2 }} onChange={handleTabChange}>
                <Tab
                    value="status.all"
                    label={t('All contacts')}
                    icon={<TabIconBadge value={initialData ? initialData.count.contacts_total : 0} icon={<ContactsIcon />} />}
                />
                {initialData && Object.entries(initialData.count.contacts_by_status).map(([key, value]) =>
                    key === '' || key === 'error' || key === 'done' || key === 'unsubscribed'
                        ? null
                        : <Tab
                            value={`status.${key}`}
                            key={key}
                            label={key}
                            icon={<TabIconBadge value={value} icon={<StatusIcon />} />}
                        />
                )}
                {initialData && 'done' in initialData.count.contacts_by_status &&
                    <Tab
                        value="status.done"
                        label={t('Done')}
                        icon={<TabIconBadge value={initialData.count.contacts_by_status.done} icon={<SuccessIcon />} />}
                    />
                }
                {initialData && 'error' in initialData.count.contacts_by_status &&
                    <Tab
                        value="status.error"
                        label={t('Error')}
                        icon={<TabIconBadge value={initialData.count.contacts_by_status.error} icon={<ErrorIcon />} />}
                    />
                }
                {initialData && 'unsubscribed' in initialData.count.contacts_by_status &&
                    <Tab
                        value="status.unsubscribed"
                        label={t('Unsubscribed')}
                        icon={<TabIconBadge value={initialData.count.contacts_by_status.unsubscribed} icon={<ErrorIcon />} />}
                    />
                }
                <Divider orientation="vertical" variant="middle" flexItem />
                {initialData && Object.entries(initialData.count.messages_by_type).map(([key, value]) => value > 0 &&
                    <Tab
                        value={`messages.${key}`}
                        key={key}
                        label={t(`credit.${key}`)}
                        icon={<TabIconBadge value={value} icon={<PackTypeIcon type={key} />} />}
                    />
                )}
            </Tabs>

            {tabType === 'status' && tabSubType &&
                <ContactTable
                    url={`/sendouts/${id}/contacts?${num}&status=${tabSubType === 'all' ? '' : tabSubType}`}
                    storageKey="contactsOfSendout"
                    withSendoutColumns={true}
                    withDeleteButton={false}
                    onRowClick={onContactClick}
                    sx={{ mb: 3 }} />
            }
            {tabType === 'messages' && tabSubType &&
                <MessageTable
                    url={`/sendouts/${id}/messages?${num}&type=${tabSubType}`}
                    type={tabSubType}
                    onRowClick={onMessageClick}
                    sx={{ mb: 3 }} />
            }
            {initialData && initialData.count.contacts_deleted > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {initialData.count.contacts_deleted > 1
                        ? t('{{count}} contacts have been deleted since the creation of this sendout',
                            { count: initialData.count.contacts_deleted })
                        : t('{{count}} contact has been deleted since the creation of this sendout',
                            { count: initialData.count.contacts_deleted })
                    }
                </Alert>
            )}
        </Layout>
    );
}

export default SendoutEditPage;
